import { useEffect, useRef, useState } from 'react';

import { ToolBaseTypes, ToolTypes } from '@labradorsports/constants';
import { usePlayEditor, useFieldViewport, useSelected, applyOffset } from '@labradorsports/play-rendering';
import { useInterval } from '@labradorsports/utils';

import { usePlugins } from '../providers/index.js';

export default function useArrowKeys() {
    const { PlayConfig, fieldSetup } = usePlayEditor();
    const { fieldViewport } = useFieldViewport();

    const { selected } = useSelected();

    const { updatePiece } = usePlugins();

    const [keysDown, setKeysDown] = useState<string[]>([]);

    const keyDownHandler = useRef<AnyFunction>();
    const keyUpHandler = useRef<AnyFunction>();

    keyDownHandler.current = (event) => {
        if (!keysDown.includes(event.key)) {
            setKeysDown(keysDown.concat([event.key]));
        }
    };

    keyUpHandler.current = (event) => {
        setKeysDown(keysDown.filter((k) => k !== event.key));
    };

    useEffect(() => {
        const keyDown = (event) => {
            keyDownHandler.current(event);
        };

        const keyUp = (event) => {
            keyUpHandler.current(event);
        };

        window.addEventListener('keydown', keyDown);
        window.addEventListener('keyup', keyUp);

        return () => {
            window.removeEventListener('keydown', keyDown);
            window.removeEventListener('keyup', keyUp);
        };
    }, []);

    useInterval(
        () => {
            const isMovable = ([ToolBaseTypes.PIECE, ToolBaseTypes.PLAYER] as string[]).includes(
                PlayConfig.MoveConfig[selected.type]?.baseType
            );
            const isShading = [ToolTypes.ELLIPSE, ToolTypes.RECTANGLE].includes(selected.type);

            if (selected && ((fieldSetup && isMovable) || isShading)) {
                const movement = {
                    x: 0,
                    y: 0,
                };

                if (keysDown.includes('ArrowRight')) {
                    movement.x += fieldViewport.getFieldLength(2);
                }

                if (keysDown.includes('ArrowLeft')) {
                    movement.x += fieldViewport.getFieldLength(-2);
                }

                if (keysDown.includes('ArrowUp')) {
                    movement.y += fieldViewport.getFieldLength(-2);
                }

                if (keysDown.includes('ArrowDown')) {
                    movement.y += fieldViewport.getFieldLength(2);
                }

                if (fieldViewport.flippedX) {
                    movement.x = -movement.x;
                }
                if (fieldViewport.flipped) {
                    movement.y = -movement.y;
                }

                if (movement.x !== 0 || movement.y !== 0) {
                    updatePiece(
                        {
                            id: selected.id,
                            type: selected.type,
                            pointerType: 'keyboard',
                        },
                        applyOffset(selected.origin, movement)
                    );
                }
            }
        },
        selected && keysDown.length > 0 ? 100 : null,
        true
    );
}
